import { useCallback, useEffect, useMemo } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiBox,
  MiButton,
  MiInputSelectBox,
  MiMenu,
  MiModal,
  MiPlaceholderList,
  MiSectionHeader,
  PriceRollup,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  ICONS_WEB,
} from "@miplatform/constants";
import { filterMaterialsByAttributes } from "@miplatform/utils";
import * as mdi from "@mdi/js";
import { SearchResultsContainer } from "@mibuildweb/components";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Skeleton,
  Typography,
  useTheme,
} from "@mui/material";
import { CollapsibleIcon } from "../../../../components/CollapsibleIcon/CollapsibleIcon";
import { AREA_COLUMNS } from "../../../constants";
import { ItemTemplatePlanArea } from "../ItemTemplatePlanArea/ItemTemplatePlanArea";
import { MaterialDrawer } from "../MaterialDrawer/MaterialDrawer";
import { PlanArea } from "../PlanArea/PlanArea";
import { AreaDrawer } from "./AreaDrawer/AreaDrawer";
import { MaterialResultCard } from "./MaterialResultCard/MaterialResultCard";
import { useSpecAreaFormViewModel } from "./useSpecAreaFormViewModel";
import { useSpecMaterialFormViewModel } from "./useSpecMaterialFormViewModel";

export function SpecConfigForm({
  stateManager,
  isLoading,
  activeStep,
  planDetails,
  priceRollup,
  areaTypes,
  areaTemplates,
  getAreaTemplates,
  setRequiredCategoriesMatch,
  handleAddItems,
  handleAddAreas,
  handleUpdateArea,
  handleRemoveArea,
  handleRemoveFloor,
  handleCreateSpecPlan,
}) {
  const {
    showAreaDrawer,
    areaData,
    planFloors,
    tempFloors,
    availableFloors,
    collapsedFloors,
    floorToAdd,
    selectedFloor,
    selectedAreaId,
    applyingAreaTemplate,
    showAreaTemplateModal,
    selectedAreaTemplate,
    dismissedOptionalCategories,
    setAreaData,
    setPlanFloors,
    setTempFloors,
    setFloorToAdd,
    setSelectedAreaId,
    setApplyingAreaTemplate,
    setShowAreaTemplateModal,
    setSelectedAreaTemplate,
    getFloors,
    removeFloor,
    handleAddFloor,
    handleOpenAddAreaDrawer,
    handleCloseAreaDrawer,
    handleAddAreaData,
    handleCollapseFloor,
    handleDismissCategory,
  } = useSpecAreaFormViewModel({
    stateManager,
    handleAddAreas,
    handleRemoveFloor,
    planDetails,
  });

  const {
    selectedItems,
    checkedAreas,
    materialOptions,
    categoryFilterOptions,
    selectedCategory,
    selectedAttributeValues,
    selectedPlaceholder,
    search,
    isSuppliedByBuilder,
    showCategoryFilter,
    debouncedSearch,
    debouncedItemTemplateSearch,
    hasFilters,
    materialResults,
    noResultsHelperText,
    openMaterialResults,
    itemTemplatesResult,
    selectedItemTemplate,
    setSelectedItemTemplate,
    itemTemplateSearch,
    templateFilter,
    seriesOptions,
    filteredAreaTypeOptions,
    openItemTemplateResults,
    getSeriesOptions,
    getAreaTypeOptions,
    getItemTemplates,
    handleAreaTypeOptionFilter,
    handleSeriesOptionFilter,
    setOpenItemTemplateResults,
    resetMaterialDrawerStates,
    setSelectedPlaceholder,
    setOpenMaterialResults,
    setShowCategoryFilter,
    setSelectedItems,
    setIsSuppliedByBuilder,
    handleCloseItemTemplateResults,
    setFilteredMaterialOptions,
    setCheckedAreas,
    getCategoryFilters,
    getMaterialOptions,
    handleCheckArea,
    handleAddItemToAreas,
    handleAddItemTemplatesToAreas,
    handleResetMaterialOptions,
    handleResetAddItems,
    handleCategoryClick,
    handleAttributeValueClick,
    handleItemTemplateSearch,
    handleItemSearch,
  } = useSpecMaterialFormViewModel({
    stateManager,
    handleAddItems,
    planDetails,
  });

  const drawerOpen = useMemo(
    () =>
      showAreaDrawer ||
      showCategoryFilter ||
      openMaterialResults ||
      openItemTemplateResults,
    [
      showAreaDrawer,
      showCategoryFilter,
      openMaterialResults,
      openItemTemplateResults,
    ]
  );

  const { classes } = useStyles();
  const theme = useTheme();

  const previewAreas = useMemo(() => {
    const areasToDisplay = selectedAreaTemplate?.planAreas.length
      ? selectedAreaTemplate?.planAreas
      : areaData;

    return (
      areasToDisplay?.map((area) => {
        // if the area is part of the selected template, use its floorId; otherwise, use the select floor's id
        const previewArea = {
          ...area,
          floorId: selectedAreaTemplate?.planAreas.length
            ? area.floorId
            : selectedFloor.id,
          preview: true,
        };
        delete previewArea.id;
        return previewArea;
      }) || []
    );
  }, [areaData, selectedAreaTemplate]);

  const sortedPlanAreas = planDetails.planAreas?.sort(
    (a, b) => a.displayOrder - b.displayOrder
  );

  const groupAreasByFloor = (areas) => {
    return areas.length ? Object.groupBy(areas, (area) => area.floorId) : {};
  };

  const groupedAreas = groupAreasByFloor(
    (sortedPlanAreas ?? []).concat(previewAreas)
  );

  const title = "Add Areas & Items";
  const subtitle =
    "Add to the Areas by selecting Add Items or use a pre-saved template.";

  useEffect(() => {
    handleResetAddItems();
    setOpenMaterialResults(false);
    getCategoryFilters();
    getSeriesOptions();
    getAreaTypeOptions();
    getAreaTemplates();
    getFloors();
  }, [activeStep]);

  useEffect(() => {
    if (
      availableFloors?.length ||
      sortedPlanAreas?.length > 0 ||
      (previewAreas?.length >= 0 && applyingAreaTemplate)
    ) {
      // derive saved floors from plan areas
      const planAreaFloors = availableFloors?.length
        ? Object.keys(groupAreasByFloor(sortedPlanAreas))?.map((key) =>
            availableFloors?.find((floor) => {
              return floor?.id === key;
            })
          )
        : [];
      setPlanFloors(planAreaFloors);

      // determine any floors to be displayed that aren't currently saved as part of the plan
      const previewFloors = Object.keys(groupAreasByFloor(previewAreas))?.map(
        (key) =>
          availableFloors?.find((floor) => {
            return floor?.id === key;
          })
      );
      setTempFloors((prev) => {
        const existingTempFloorIds = new Set(prev.map((f) => f?.id));
        const existingPlanAreaFloorIds = new Set(
          planAreaFloors.map((f) => f?.id)
        );
        // filter out any duplicate existing floors
        const newFloors = previewFloors.filter(
          (f) =>
            f &&
            !existingTempFloorIds.has(f?.id) &&
            !existingPlanAreaFloorIds.has(f?.id)
        );
        return [...prev, ...newFloors];
      });
    }
  }, [previewAreas?.length, availableFloors?.length]);

  useEffect(() => {
    if (debouncedSearch || selectedCategory?.categoryId) {
      getMaterialOptions();
    }
    if ((!debouncedSearch || !selectedCategory?.categoryId) && !isLoading) {
      handleResetMaterialOptions();
    }
  }, [debouncedSearch, selectedCategory]);

  useEffect(() => {
    if (
      selectedCategory?.categoryId &&
      Object.keys(selectedAttributeValues).length > 0
    ) {
      // filter displayed material options by attribute value(s)
      setFilteredMaterialOptions(
        filterMaterialsByAttributes(materialOptions, selectedAttributeValues)
      );
    } else {
      // reset filtered material options to results from selected category and/or search term
      setFilteredMaterialOptions(materialOptions);
    }
  }, [materialOptions, selectedAttributeValues]);

  useEffect(() => {
    if (showCategoryFilter) {
      getItemTemplates();
    }
  }, [templateFilter, debouncedItemTemplateSearch]);

  const handleOpenCategoryFilter = useCallback((area) => {
    setCheckedAreas([area]);
    setShowCategoryFilter(true);
  }, []);

  const handleCloseCategoryFilter = useCallback(() => {
    setSelectedAreaId(null);
    setCheckedAreas([]);
    setShowCategoryFilter(false);
    resetMaterialDrawerStates();
    setSelectedPlaceholder(null);
  }, []);

  const renderLeftOrRightColumn = (planAreas, column) => {
    return (
      planAreas?.length > 0 &&
      planAreas.map((area) => {
        const sortedAreaIndex = sortedPlanAreas.findIndex(
          (sortedArea) => sortedArea.id === area.id
        );
        const sortedPlanDetails = {
          ...planDetails,
          planAreas: planDetails.planAreas.sort((a, b) => a.floor - b.floor),
        };
        if (
          area.specColumn === column ||
          area.areaType?.specColumn === column
        ) {
          const areaType = areaTypes.find(
            (areaType) => areaType.id === area.areaTypeId
          );
          const dismissedCategories =
            dismissedOptionalCategories?.[area.id] ?? [];

          return (
            <PlanArea
              key={area.label + area.id}
              area={area}
              areaType={areaType}
              updateArea={handleUpdateArea}
              planDetails={sortedPlanDetails}
              selectedAreaId={selectedAreaId}
              setSelectedAreaId={setSelectedAreaId}
              dismissedCategories={dismissedCategories}
              handleDismissCategory={handleDismissCategory}
              checkedAreas={checkedAreas}
              handleCheckArea={handleCheckArea}
              handleRemoveArea={handleRemoveArea}
              handleResetAddItems={handleResetAddItems}
              handleCreateSpecPlan={handleCreateSpecPlan}
              selectedPlaceholder={selectedPlaceholder}
              setSelectedPlaceholder={setSelectedPlaceholder}
              categoryFilterOptions={categoryFilterOptions}
              setRequiredCategoriesMatch={setRequiredCategoriesMatch}
              handleCategoryClick={handleCategoryClick}
              handleOpenCategoryFilter={() => handleOpenCategoryFilter(area)}
              showAreaDrawer={showAreaDrawer}
              showCategoryFilter={showCategoryFilter}
              openMaterialResults={openMaterialResults}
              setOpenMaterialResults={setOpenMaterialResults}
              openItemTemplateResults={openItemTemplateResults}
              isLoading={isLoading}
              stateManager={stateManager}
              index={sortedAreaIndex}
            />
          );
        }
        return null;
      })
    );
  };

  const combinedFloors = [...planFloors, ...tempFloors]
    .filter(
      (floor, index, arr) => arr.findIndex((f) => f?.id === floor?.id) === index
    )
    .sort((a, b) => a.displayOrder - b.displayOrder);
  const noAvailableFloors = availableFloors.length === 0;
  const allFloorsAdded = availableFloors.length === combinedFloors.length;

  return (
    <Box className={classes.root}>
      <MiBox className={classes.container} height="auto">
        <MiSectionHeader
          title={title}
          subtitle={subtitle}
          rightAction={<PriceRollup data={priceRollup} />}
        />
        <Box className={classes.overflowContainer}>
          {combinedFloors?.length > 0 &&
            combinedFloors.map((floor) => {
              const isCollapsed = collapsedFloors[floor?.id];
              return (
                <Accordion
                  key={floor?.id}
                  className={classes.floorCardContainer}
                  expanded={!isCollapsed}
                  disableGutters
                  style={{
                    border:
                      selectedFloor?.id === floor?.id && !applyingAreaTemplate
                        ? `2px solid ${theme.palette.orange.main}`
                        : "2px solid transparent",
                  }}
                >
                  <AccordionSummary>
                    <Box
                      onClick={(e) => e.stopPropagation()}
                      className={classes.accordionSummaryBox}
                    >
                      <Box>
                        {floor?.name ? (
                          <Typography
                            fontWeight={theme.fontWeight.bold}
                            paddingLeft={theme.spacing(2)}
                          >
                            {`${floor?.name} - ${floor?.displayOrder}`}
                          </Typography>
                        ) : (
                          <Skeleton
                            variant="text"
                            sx={{ fontSize: "2rem" }}
                            width={200}
                          />
                        )}
                      </Box>
                      <Box className={classes.floorCardHeaderContainer}>
                        <MiButton
                          title="Add Area"
                          variant={BUTTON_VARIANTS.TEXT}
                          startIcon={{
                            path: mdi[ICONS_WEB.PLUS],
                            iconSize: 0.8,
                          }}
                          onClick={() => handleOpenAddAreaDrawer(floor)}
                          disabled={showCategoryFilter || isLoading}
                        />
                        <Typography variant="body1">
                          Areas: {groupedAreas[floor?.id]?.length || 0}
                        </Typography>
                        <CollapsibleIcon
                          isCollapsed={!isCollapsed}
                          onCollapse={() => handleCollapseFloor(floor?.id)}
                        />
                        <MiMenu
                          data={[
                            {
                              label: "Remove Floor",
                              icon: mdi[ICONS_WEB.TRASH_CAN],
                              disabled: drawerOpen || isLoading,
                              action: () => removeFloor(floor),
                            },
                          ]}
                        />
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails className={classes.areaColumnsContainer}>
                    <Box className={classes.areaColumnContainer}>
                      {renderLeftOrRightColumn(
                        groupedAreas[floor?.id],
                        AREA_COLUMNS.LEFT
                      )}
                    </Box>
                    <Box className={classes.areaColumnContainer}>
                      {renderLeftOrRightColumn(
                        groupedAreas[floor?.id],
                        AREA_COLUMNS.RIGHT
                      )}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          {combinedFloors.length === 0 ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="100%"
            >
              <MiPlaceholderList
                text={
                  !noAvailableFloors ? "No Floors Added" : "No Floors Exist"
                }
                helperText={
                  !noAvailableFloors
                    ? "Select a Floor and click the + Floor button to add a floor to this bid"
                    : "Please create a Floor in Configuration to add it to this bid"
                }
              />
            </Box>
          ) : null}
          <Box className={classes.bottomActionContainer}>
            <Box className={classes.addFloorContainer}>
              <Typography variant="caption">
                Click on the + Floor button to add a floor to this bid
              </Typography>
              <Box className={classes.addFloorBtns}>
                <MiInputSelectBox
                  name="floor-select"
                  label="Select Floor"
                  value={floorToAdd?.id ?? null}
                  handleChange={(e) => {
                    const { value } = e.target;
                    if (value === "None") {
                      setFloorToAdd(null);
                      return;
                    }
                    setFloorToAdd(availableFloors.find((f) => f.id === value));
                  }}
                  options={availableFloors.map((floor) => ({
                    label: `${floor?.name} - ${floor?.displayOrder}`,
                    value: floor?.id,
                    disabled: planFloors.some((f) => f?.id === floor?.id),
                  }))}
                  disabled={
                    noAvailableFloors ||
                    allFloorsAdded ||
                    showCategoryFilter ||
                    showAreaDrawer
                  }
                />
                <MiButton
                  variant={BUTTON_VARIANTS.OUTLINED}
                  startIcon={{ path: mdi[ICONS_WEB.PLUS], iconSize: 0.8 }}
                  onClick={handleAddFloor}
                  title="Floor"
                  disabled={
                    floorToAdd === null || showCategoryFilter || showAreaDrawer
                  }
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </MiBox>
      <AreaDrawer
        open={showAreaDrawer}
        onClose={handleCloseAreaDrawer}
        planAreas={sortedPlanAreas ?? []}
        areaData={areaData}
        areaTemplates={areaTemplates}
        selectedAreaTemplate={selectedAreaTemplate}
        setSelectedAreaTemplate={setSelectedAreaTemplate}
        setAreaData={setAreaData}
        selectedFloor={selectedFloor}
        setApplyingAreaTemplate={setApplyingAreaTemplate}
        actions={{
          emptyAreas: [
            {
              variant: BUTTON_VARIANTS.CONTAINED,
              name: `Add Areas to ${selectedFloor?.name}`,
              disabled: !areaData.length,
              onClick: handleAddAreaData,
              startIcon: { path: mdi[ICONS_WEB.PLUS] },
            },
          ],
          areaTemplates: [
            {
              variant: BUTTON_VARIANTS.CONTAINED,
              name: "Apply Area Template",
              disabled: !selectedAreaTemplate,
              onClick: () => {
                if (planDetails.planAreas.length > 0) {
                  setShowAreaTemplateModal(true);
                } else {
                  handleAddAreas(previewAreas);
                  handleCloseAreaDrawer();
                }
              },
              startIcon: { path: mdi[ICONS_WEB.PLUS] },
            },
          ],
        }}
      />
      <SearchResultsContainer
        open={openMaterialResults}
        onClose={() => {
          handleResetAddItems();
          setOpenMaterialResults(false);
          setSelectedPlaceholder(null);
        }}
        alertTitle="Select Item(s) and add to Area(s)."
        alertDescription="After selecting the item, choose the Area(s) and select the Add to Area(s) button."
        storageKey="planMaterials-alert"
        isLoading={isLoading}
        results={materialResults}
        noResultsHelperText={noResultsHelperText}
        hasFilters={hasFilters}
        handleResetFilterSelection={handleResetAddItems}
        renderResults={() =>
          materialResults.map((material) => (
            <MaterialResultCard
              key={material.id}
              material={material}
              selectedItems={selectedItems}
              setSelectedItems={setSelectedItems}
              checkedAreas={checkedAreas}
              handleAddItemToAreas={handleAddItemToAreas}
              setIsSuppliedByBuilder={setIsSuppliedByBuilder}
              isSuppliedByBuilder={isSuppliedByBuilder}
              planAreas={planDetails.planAreas}
              handleCheckArea={handleCheckArea}
            />
          ))
        }
      />
      <SearchResultsContainer
        open={openItemTemplateResults}
        onClose={handleCloseItemTemplateResults}
        alertTitle="Select Item Template(s) and add to area(s)."
        alertDescription="After selecting the item(s), choose the Area(s) and select the Add Selected Items button."
        storageKey="itemTemplatesMaterials-alert"
        isLoading={isLoading}
        results={itemTemplatesResult}
        hasFilters={hasFilters}
        renderResults={() =>
          itemTemplatesResult.map((template) => (
            <ItemTemplatePlanArea
              key={template.planAreaId}
              itemTemplate={template}
              checkedAreas={checkedAreas}
              setSelectedItemTemplate={setSelectedItemTemplate}
              selectedItemTemplate={selectedItemTemplate}
              openItemTemplateResults={openItemTemplateResults}
              selectedItems={selectedItems}
              setSelectedItems={setSelectedItems}
              handleAddItemTemplatesToAreas={handleAddItemTemplatesToAreas}
            />
          ))
        }
      />
      <MaterialDrawer
        open={showCategoryFilter}
        onClose={handleCloseCategoryFilter}
        planDetails={planDetails}
        resetMaterialDrawerStates={resetMaterialDrawerStates}
        // Category filter props
        search={search}
        handleItemSearch={handleItemSearch}
        categoryFilterOptions={categoryFilterOptions}
        selectedCategory={selectedCategory}
        selectedAttributeValues={selectedAttributeValues}
        handleCategoryClick={handleCategoryClick}
        handleAttributeValueClick={handleAttributeValueClick}
        // item template filter props
        itemTemplateSearch={itemTemplateSearch}
        handleItemTemplateSearch={handleItemTemplateSearch}
        templateFilter={templateFilter}
        seriesOptions={seriesOptions}
        filteredAreaTypeOptions={filteredAreaTypeOptions}
        handleSeriesOptionFilter={handleSeriesOptionFilter}
        handleAreaTypeOptionFilter={handleAreaTypeOptionFilter}
        setOpenItemTemplateResults={setOpenItemTemplateResults}
      />
      <MiModal
        open={showAreaTemplateModal}
        title="Apply Area Template"
        description="Select whether to replace existing areas with the current template or to apply it alongside existing areas."
        closeModal={() => setShowAreaTemplateModal(false)}
        width={500}
        actions={[
          {
            name: "Apply & Replace",
            variant: BUTTON_VARIANTS.OUTLINED,
            color: BUTTON_COLORS.PRIMARY,
            onClick: () => {
              handleAddAreas(previewAreas, true);
              handleCloseAreaDrawer();
            },
          },
          {
            name: "Apply With Existing",
            variant: BUTTON_VARIANTS.CONTAINED,
            color: BUTTON_COLORS.PRIMARY,
            onClick: () => {
              handleAddAreas(previewAreas);
              handleCloseAreaDrawer();
            },
          },
        ]}
      />
    </Box>
  );
}

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    overflow: "auto",
    height: "100%",
  },
  container: {
    width: "100%",
    flex: 1,
    display: "flex",
    flexDirection: "column",
  },
  floorCardContainer: {
    backgroundColor: theme.palette.primary.light,
    marginBottom: theme.spacing(2),
    "&::before": {
      content: "none",
    },
  },
  floorCardHeaderContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  areaColumnContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  bottomActionContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-end",
    alignItems: "center",
    flex: 1,
  },
  addFloorContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
    width: "100%",
    gap: theme.spacing(3),
    padding: theme.spacing(4),
    border: `1px solid ${theme.palette.grey.accent}`,
    borderRadius: 4,
  },
  addFloorBtns: {
    display: "flex",
    width: 300,
    gap: theme.spacing(2),
  },
  areaColumnsContainer: {
    display: "flex",
    gap: 8,
    "@container (min-width: 1200px)": {
      flexWrap: "nowrap",
    },
    "@container (max-width: 1199px)": {
      flexWrap: "wrap",
    },
  },
  overflowContainer: {
    display: "flex",
    flexDirection: "column",
    overflow: "auto",
    flex: 1,
    containerType: "inline-size",
    "& .MuiAccordion-root": {
      borderRadius: 8,
    },
    "& .MuiAccordionSummary-root:hover": {
      cursor: "default",
    },
  },
  accordionSummaryBox: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
  },
  areaTemplateModal: {
    paddingBottom: theme.spacing(3),
  },
}));
